import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TRecommendation } from '../../types/recommendation';
import {
  getMobileRecommendations,
  getDesktopRecommendations,
  TRecommendationsResult,
} from '../../services/getRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { createAppAsyncThunk } from '../../utils/redux/createAppAsyncThunk';
import { DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT } from '../../constants/experiments';
import { TAbUseExperimentsState } from '../../types/abUseExperiments';
import { isAbUseExperimentGroup } from '../../utils/isAbUseExperimentGroup';

const INITIAL_PAGE = 0;
const REDESIGN_EXPERIMENT_GROUP = 'B';

export enum EPaginationStatus {
  Idle = 'idle',
  Loading = 'loading',
  Error = 'error',
}

const initialState = {
  data: [] as TRecommendation[],
  status: ELoadingStatus.Loading,
  page: INITIAL_PAGE,
  hasMore: false,
  totalCount: null as number | null,
  paginationStatus: EPaginationStatus.Idle,
};

function isDesktopPaginationEnabled(isMobile: boolean, experiments: TAbUseExperimentsState) {
  return (
    !isMobile &&
    isAbUseExperimentGroup(experiments, DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT, REDESIGN_EXPERIMENT_GROUP)
  );
}

export const fetchRecommendations = createAppAsyncThunk('recommendations/fetch', (_, { getState, extra: context }) => {
  const {
    custom: { isMobile },
  } = context;
  const { regionId, landingType, liquiditySource, experiments = [] } = getState();
  const usePagination = isMobile || isDesktopPaginationEnabled(isMobile, experiments);
  const parameters = {
    regionId,
    landingType,
    ...(liquiditySource ? { liquiditySource } : null),
    ...(usePagination ? { page: INITIAL_PAGE } : null),
  };

  const getRecommendations = isMobile ? getMobileRecommendations : getDesktopRecommendations;

  return getRecommendations(context, { parameters, offset: INITIAL_PAGE });
});

type TFetchMoreRecommendationsPayload = TRecommendationsResult & {
  page: number;
};

export const fetchMoreRecommendations = createAppAsyncThunk(
  'recommendations/fetchMore',
  async (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource, recommendations, experiments = [] } = getState();
    const useDesktopPagination = isDesktopPaginationEnabled(isMobile, experiments);

    if (!isMobile && !useDesktopPagination) {
      return {
        recommendations: [],
        hasMore: false,
        ...(recommendations.totalCount !== null && recommendations.totalCount !== undefined
          ? { totalCount: recommendations.totalCount }
          : null),
        page: recommendations.page,
      } as TFetchMoreRecommendationsPayload;
    }

    const nextPage = recommendations.page + 1;
    const parameters = {
      regionId,
      landingType,
      page: nextPage,
      ...(liquiditySource ? { liquiditySource } : null),
    };
    const response = isMobile
      ? await getMobileRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        })
      : await getDesktopRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        });

    return {
      ...response,
      page: nextPage,
    };
  },
);

type TToggleFavoritePayload = {
  offerId: number;
  isFavorite: boolean;
};

const recommendationsSlice = createSlice({
  name: 'recommendations',
  reducers: {
    toggleFavorite: (state, action: PayloadAction<TToggleFavoritePayload>) => {
      state.data.forEach(recommendation => {
        if (recommendation.offerId === action.payload.offerId) {
          recommendation.isFavorite = action.payload.isFavorite;
        }
      });
    },
  },
  initialState,
  extraReducers(builder) {
    builder.addCase(fetchRecommendations.pending, state => {
      state.status = ELoadingStatus.Loading;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, { payload }) => {
      state.status = ELoadingStatus.Success;
      state.data = payload.recommendations;
      state.page = INITIAL_PAGE;
      state.hasMore = payload.hasMore;
      state.totalCount = payload.totalCount ?? null;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.rejected, state => {
      state.status = ELoadingStatus.Failure;
      state.hasMore = false;
      state.totalCount = null;
      state.page = INITIAL_PAGE;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.pending, state => {
      state.paginationStatus = EPaginationStatus.Loading;
    });
    builder.addCase(fetchMoreRecommendations.fulfilled, (state, { payload }) => {
      state.data = [...state.data, ...payload.recommendations];
      state.page = payload.page;
      state.hasMore = payload.hasMore;
      state.totalCount = payload.totalCount ?? state.totalCount;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.rejected, state => {
      state.paginationStatus = EPaginationStatus.Error;
    });
  },
});

export const recommendationsReducer = recommendationsSlice.reducer;
export const { toggleFavorite } = recommendationsSlice.actions;
